<template>
  <div class="hms-page">
    <Navigation />

    <div class="container-fluid hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-doctor me-2"></i>{{ t('consult.title','Consultation') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-blue" @click="$router.push('/triage')">
            <i class="mdi mdi-stethoscope me-1"></i>Triage
          </button>
          <button class="hms-btn hms-btn-blue" @click="$router.push('/ed-board')">
            <i class="mdi mdi-hospital me-1"></i>ED Board
          </button>
          <button class="hms-btn hms-btn-blue" @click="loadQueue" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <div class="row g-3">
        <!-- LEFT — Queue -->
        <div class="col-lg-3">
          <div class="hms-card">
            <div class="hms-tabs hms-tabs-sm mb-2">
              <button class="hms-tab" :class="{ active: scope==='all' }"  @click="scope='all'; loadQueue()">All</button>
              <button class="hms-tab" :class="{ active: scope==='ed' }"   @click="scope='ed'; loadQueue()">ED</button>
              <button class="hms-tab" :class="{ active: scope==='opd' }"  @click="scope='opd'; loadQueue()">OPD</button>
              <button class="hms-tab" :class="{ active: scope==='mine' }" @click="scope='mine'; loadQueue()">Mine</button>
            </div>

            <input class="hms-input mb-2" v-model="queueSearch"
                   placeholder="🔍 Search name / patient # / phone / visit #"
                   @input="debouncedQueueLoad" />

            <div class="hms-consult-filter mb-2">
              <div class="d-flex gap-1 flex-wrap align-items-center">
                <label class="hms-consult-flbl">From</label>
                <input type="date" class="hms-input hms-consult-dt" v-model="filterFrom" :max="filterTo || todayIso" @change="loadQueue" />
                <label class="hms-consult-flbl">To</label>
                <input type="date" class="hms-input hms-consult-dt" v-model="filterTo" :min="filterFrom" :max="todayIso" @change="loadQueue" />
              </div>
              <div class="d-flex gap-1 flex-wrap align-items-center mt-1">
                <button type="button" class="hms-chip" :class="{ active: isTodayFilter }" @click="setFilterToday">Today</button>
                <button type="button" class="hms-chip" @click="setFilterYesterday">Yesterday</button>
                <button type="button" class="hms-chip" @click="setFilterWeek">Last 7d</button>
              </div>
            </div>

            <div class="d-flex align-items-center justify-content-between mb-1">
              <div class="hms-section-label">QUEUE ({{ queue.length }})</div>
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="callNextPatient" :disabled="callingNext" title="Call the next waiting patient in the consultation queue">
                <i class="mdi mdi-bullhorn-outline me-1"></i>{{ callingNext ? 'Calling…' : 'Call next' }}
              </button>
            </div>
            <div v-if="!queue.length" class="hms-empty-mini">
              <i class="mdi mdi-checkbox-marked-circle-outline"></i>No patients waiting.
            </div>
            <div v-else class="hms-q-list">
              <div v-for="q in queue" :key="q.visit_id"
                   class="hms-q-item" :class="{ active: visit && visit.visit_id === q.visit_id, 'is-ed': q.visit_type === 'emergency' }"
                   @click="selectVisit(q.visit_id)">
                <div class="d-flex align-items-center gap-2">
                  <span v-if="q.visit_type === 'emergency'" class="hms-acuity-pill" :class="'acu-' + (q.triage_level || 'unset')">{{ acuityShort(q.triage_level) }}</span>
                  <span class="hms-mono hms-q-num">{{ q.visit_number }}</span>
                  <span class="hms-q-time" v-if="q.arrival_time"><i class="mdi mdi-timer-outline"></i> {{ minutesIn(q.arrival_time) }}m</span>
                </div>
                <div class="hms-q-name">{{ q.first_name }} {{ q.last_name }}
                  <span v-if="Number(q.labs_ready)" class="hms-badge hms-badge-lab ms-1"><i class="mdi mdi-flask-outline"></i> LAB RESULTS READY</span>
                </div>
                <div class="hms-muted-sm">{{ q.patient_no }} · {{ ageStr(q.dob) }} · {{ q.gender }}</div>
                <div class="hms-q-complaint" v-if="q.chief_complaint">"{{ truncate(q.chief_complaint, 50) }}"</div>
                <div v-if="q.allergies" class="hms-warn-strip"><i class="mdi mdi-alert-octagon"></i> {{ truncate(q.allergies, 40) }}</div>
                <div v-if="q.bay_name" class="hms-muted-sm"><i class="mdi mdi-bed"></i> {{ q.bay_name }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT — Encounter -->
        <div class="col-lg-9">
          <div v-if="!visit" class="hms-card hms-empty">
            <i class="mdi mdi-account-arrow-left-outline"></i>
            Select a patient from the queue to start the consultation.
          </div>

          <div v-else>
            <!-- Patient summary header -->
            <div class="hms-card mb-3">
              <div class="d-flex align-items-start gap-3 flex-wrap">
                <div class="hms-avatar-lg">{{ initials(visit) }}</div>
                <div class="flex-fill">
                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <span class="hms-h">{{ visit.first_name }} {{ visit.last_name }}</span>
                    <span class="hms-mono hms-muted-sm">{{ visit.patient_no }}</span>
                    <span class="hms-badge hms-badge-info">{{ visit.visit_type }}</span>
                    <span v-if="visit.triage_level" class="hms-acuity-pill ms-1" :class="'acu-' + visit.triage_level">{{ acuityShort(visit.triage_level) }}</span>
                    <span v-if="admission" class="hms-badge hms-badge-purple">ADMITTED · {{ admission.ward_name }} · Bed {{ admission.bed_number }}</span>
                  </div>
                  <div class="hms-muted-sm mt-1">
                    {{ ageStr(visit.dob) }} · {{ visit.gender }} · BG {{ visit.blood_group || '—' }} · {{ visit.phone || '—' }}
                    <span v-if="visit.bay_name"> · <i class="mdi mdi-bed"></i> {{ visit.bay_name }}</span>
                  </div>
                  <!-- Allergies strip with inline edit -->
                  <div v-if="!allergyEdit.open" class="hms-allergy-row mt-2">
                    <div v-if="hasRealAllergy(visit.allergies)" class="hms-warn-strip flex-grow-1">
                      <i class="mdi mdi-alert-octagon"></i> Allergies: {{ visit.allergies }}
                    </div>
                    <div v-else class="hms-allergy-none flex-grow-1">
                      <i class="mdi mdi-shield-check-outline"></i> No known allergies on file
                    </div>
                    <button v-if="visit.patient_id" class="hms-btn hms-btn-sm hms-btn-ghost ms-2" @click="openAllergyEdit" title="Update allergies">
                      <i class="mdi mdi-pencil-outline"></i>
                    </button>
                  </div>
                  <div v-else class="hms-allergy-editor mt-2">
                    <label class="hms-label"><i class="mdi mdi-alert-octagon me-1"></i>Update allergies</label>
                    <input class="hms-input" v-model="allergyEdit.value" placeholder="e.g. Penicillin, Sulpha drugs — comma separated" @keyup.enter="saveAllergies" />
                    <div class="hms-muted-sm mt-1">Common:
                      <span v-for="a in commonAllergyChips" :key="a" class="hms-chip hms-chip-sm ms-1" @click="addAllergyChip(a)">+ {{ a }}</span>
                    </div>
                    <div class="d-flex gap-2 mt-2">
                      <button class="hms-btn hms-btn-sm hms-btn-primary" @click="saveAllergies" :disabled="allergyEdit.busy">
                        <span v-if="allergyEdit.busy"><span class="spinner-border spinner-border-sm me-1"></span>Saving…</span>
                        <span v-else><i class="mdi mdi-check me-1"></i>Save</span>
                      </button>
                      <button class="hms-btn hms-btn-sm" @click="closeAllergyEdit">Cancel</button>
                      <button class="hms-btn hms-btn-sm hms-btn-ghost ms-auto" @click="markNoAllergies" :disabled="allergyEdit.busy">
                        <i class="mdi mdi-shield-check-outline me-1"></i>No known allergies
                      </button>
                    </div>
                  </div>
                  <div v-if="visit.chronic_conditions" class="hms-muted-sm mt-1"><i class="mdi mdi-pulse"></i> Chronic: {{ visit.chronic_conditions }}</div>
                  <!-- Sharon F#3 #13 — Patient consent OTP for sensitive data categories -->
                  <div class="d-flex gap-2 flex-wrap mt-2">
                    <ConsentOtpButton :patient-id="visit.patient_id" category-code="hiv_art" category-label="HIV / ART" />
                    <ConsentOtpButton :patient-id="visit.patient_id" category-code="mental_health" category-label="Mental health" />
                    <ConsentOtpButton :patient-id="visit.patient_id" category-code="sgbv" category-label="SGBV" />
                    <ConsentOtpButton :patient-id="visit.patient_id" category-code="srh" category-label="SRH" />
                  </div>
                  <div class="mt-2">
                    <UnpaidBanner :patient-id="visit.patient_id" />
                  </div>
                </div>
                <div class="hms-vitals-strip" v-if="vitalsLast">
                  <div class="hms-vital-cell">
                    <div class="hms-vital-num">{{ vitalsLast.bp_systolic || '?' }}/{{ vitalsLast.bp_diastolic || '?' }}</div>
                    <div class="hms-vital-lbl">BP</div>
                  </div>
                  <div class="hms-vital-cell">
                    <div class="hms-vital-num">{{ vitalsLast.pulse || '—' }}</div>
                    <div class="hms-vital-lbl">HR</div>
                  </div>
                  <div class="hms-vital-cell">
                    <div class="hms-vital-num">{{ vitalsLast.temperature || '—' }}</div>
                    <div class="hms-vital-lbl">°C</div>
                  </div>
                  <div class="hms-vital-cell">
                    <div class="hms-vital-num">{{ vitalsLast.oxygen_saturation || '—' }}</div>
                    <div class="hms-vital-lbl">SpO₂</div>
                  </div>
                  <div class="hms-vital-cell" :class="news2Class(vitalsLast.news2_score)">
                    <div class="hms-vital-num">{{ vitalsLast.news2_score != null ? vitalsLast.news2_score : '—' }}</div>
                    <div class="hms-vital-lbl">NEWS2</div>
                  </div>
                </div>
              </div>

              <div class="d-flex gap-2 mt-3 flex-wrap">
                <button v-if="!visit.seen_by_doctor_time" class="hms-btn hms-btn-amber" @click="markSeen">
                  <i class="mdi mdi-doctor me-1"></i>Mark Seen by Me
                </button>
                <button class="hms-btn hms-btn-blue" @click="aiAssist" :disabled="aiLoading">
                  <i class="mdi mdi-robot-outline me-1"></i>{{ aiLoading ? 'Thinking...' : 'AI Clinical Assist' }}
                </button>
                <button class="hms-btn hms-btn-amber" @click="openBundles">
                  <i class="mdi mdi-clipboard-list-outline me-1"></i>Apply Bundle
                </button>
                <div class="ms-auto d-flex gap-2 flex-wrap">
                  <button class="hms-btn hms-btn-info" @click="dispose('admitted')"><i class="mdi mdi-bed me-1"></i>Admit</button>
                  <button class="hms-btn hms-btn-success" @click="dispose('discharged')"><i class="mdi mdi-home-export-outline me-1"></i>Discharge</button>
                  <button class="hms-btn hms-btn-warning" @click="dispose('referred_out')"><i class="mdi mdi-truck-fast-outline me-1"></i>Refer</button>
                </div>
              </div>

              <div v-if="aiResult" class="hms-ai-box mt-3">
                <div class="d-flex align-items-center mb-1">
                  <i class="mdi mdi-robot-outline me-1"></i><strong>AI Clinical Assist</strong>
                  <button class="hms-btn-icon ms-auto" @click="aiResult = null"><i class="mdi mdi-close"></i></button>
                </div>
                <div class="hms-ai-body">{{ aiResult }}</div>
                <div class="hms-muted-sm mt-1"><i class="mdi mdi-shield-alert-outline"></i> Verify all suggestions clinically before acting.</div>
              </div>
            </div>

            <!-- Tabs -->
            <div class="hms-tabs mb-3">
              <button class="hms-tab" :class="{ active: tab==='soap' }"      @click="tab='soap'">     <i class="mdi mdi-note-edit-outline me-1"></i>{{ t('consult.soap','Clinical Notes') }}</button>
              <button class="hms-tab" :class="{ active: tab==='vitals' }"    @click="tab='vitals'">   <i class="mdi mdi-heart-pulse me-1"></i>Vitals</button>
              <button class="hms-tab" :class="{ active: tab==='diagnoses' }" @click="tab='diagnoses'"><i class="mdi mdi-clipboard-pulse me-1"></i>{{ t('consult.diagnoses','Diagnoses') }}</button>
              <button class="hms-tab" :class="{ active: tab==='orders' }"    @click="tab='orders'">   <i class="mdi mdi-clipboard-list-outline me-1"></i>Orders</button>
              <button class="hms-tab" :class="{ active: tab==='history' }"   @click="tab='history'">  <i class="mdi mdi-history me-1"></i>History</button>
            </div>

            <!-- CLINICAL NOTES TAB (formerly SOAP — HMIS_Issues: renamed fields, same 4 sections) -->
            <div v-if="tab === 'soap'" class="hms-card">
              <!-- Triage vitals strip — HMIS_Issues: whatever was recorded at Triage must be visible to the doctor -->
              <div v-if="vitalsThis.length" class="hms-triage-vitals mb-3">
                <div class="hms-section-label">VITALS ON FILE (recorded {{ formatDateTime(vitalsThis[0].recorded_at) }} — no need to re-take)</div>
                <div class="hms-vitals-strip">
                  <span class="hms-vs-item"><i class="mdi mdi-heart-pulse"></i> BP {{ vitalsThis[0].bp_systolic }}/{{ vitalsThis[0].bp_diastolic }}</span>
                  <span class="hms-vs-item"><i class="mdi mdi-pulse"></i> HR {{ vitalsThis[0].pulse }}</span>
                  <span class="hms-vs-item"><i class="mdi mdi-lungs"></i> RR {{ vitalsThis[0].respiratory_rate }}</span>
                  <span class="hms-vs-item"><i class="mdi mdi-thermometer"></i> {{ vitalsThis[0].temperature }}°C</span>
                  <span class="hms-vs-item"><i class="mdi mdi-water-percent"></i> SpO₂ {{ vitalsThis[0].oxygen_saturation }}%</span>
                  <span class="hms-vs-item" v-if="Number(vitalsThis[0].blood_sugar)"><i class="mdi mdi-diabetes"></i> Sugar {{ vitalsThis[0].blood_sugar }}</span>
                  <span class="hms-vs-item"><span class="hms-badge" :class="news2Badge(vitalsThis[0].news2_score)">NEWS2 {{ vitalsThis[0].news2_score }}</span></span>
                </div>
              </div>

              <div v-if="editingNoteId" class="hms-alert hms-alert-amber mb-2">
                <i class="mdi mdi-pencil me-1"></i>Editing a saved note — Save will update it.
                <button class="hms-btn hms-btn-sm ms-2" @click="cancelNoteEdit">Cancel edit</button>
              </div>

              <div class="hms-section-label">PATIENT COMPLAINT &amp; HISTORY</div>
              <textarea class="hms-input mb-3" rows="4" v-model="soap.subjective" placeholder="Patient's reported symptoms, history of present illness, review of systems..."></textarea>

              <div class="hms-section-label">EXAMINATION FINDINGS</div>
              <textarea class="hms-input mb-3" rows="4" v-model="soap.objective" placeholder="Physical exam, observable findings (vitals are tracked separately)..."></textarea>

              <div class="hms-section-label">IMPRESSIONAL DIAGNOSIS</div>
              <textarea class="hms-input mb-3" rows="3" v-model="soap.assessment" placeholder="Working diagnosis and reasoning..."></textarea>

              <div class="hms-section-label">TREATMENT PLAN &amp; FOLLOW-UP</div>
              <textarea class="hms-input mb-3" rows="4" v-model="soap.plan" placeholder="Investigations to order, treatment plan, follow-up..."></textarea>

              <SaveAndQueue ref="sqSoap" from-point="consultation" :saving="processing"
                            :save-label="editingNoteId ? 'Update Note' : 'Save Note'" saving-label="Saving..."
                            btn-class="hms-btn hms-btn-primary" @save="saveSoap" />

              <div v-if="notes.length" class="mt-4">
                <div class="hms-section-label">PREVIOUS NOTES THIS VISIT ({{ notes.length }})</div>
                <div v-for="n in notes" :key="n.note_id" class="hms-prior-note">
                  <div class="hms-muted-sm d-flex align-items-center">
                    <strong>{{ n.doctor_name }}</strong>&nbsp;· {{ formatDateTime(n.created_at) }}
                    <button class="hms-btn hms-btn-sm hms-btn-ghost ms-auto" @click="editNote(n)" title="Edit this note">
                      <i class="mdi mdi-pencil-outline"></i> Edit
                    </button>
                  </div>
                  <div v-if="n.subjective"><strong>Complaint &amp; history:</strong> {{ n.subjective }}</div>
                  <div v-if="n.objective"><strong>Examination:</strong> {{ n.objective }}</div>
                  <div v-if="n.assessment"><strong>Impressional diagnosis:</strong> {{ n.assessment }}</div>
                  <div v-if="n.plan"><strong>Treatment plan:</strong> {{ n.plan }}</div>
                </div>
              </div>
            </div>

            <!-- VITALS TAB -->
            <div v-if="tab === 'vitals'" class="hms-card">
              <div class="hms-section-label">RECORD NEW VITALS</div>
              <div class="row g-3 mb-3">
                <div class="col-md-3"><label class="hms-label">BP Systolic</label>
                  <input type="number" class="hms-input" v-model.number="newVitals.bp_systolic" /></div>
                <div class="col-md-3"><label class="hms-label">BP Diastolic</label>
                  <input type="number" class="hms-input" v-model.number="newVitals.bp_diastolic" /></div>
                <div class="col-md-3"><label class="hms-label">Pulse</label>
                  <input type="number" class="hms-input" v-model.number="newVitals.pulse" /></div>
                <div class="col-md-3"><label class="hms-label">Resp Rate</label>
                  <input type="number" class="hms-input" v-model.number="newVitals.respiratory_rate" /></div>
                <div class="col-md-3"><label class="hms-label">Temp °C</label>
                  <input type="number" step="0.1" class="hms-input" v-model.number="newVitals.temperature" /></div>
                <div class="col-md-3"><label class="hms-label">SpO₂ %</label>
                  <input type="number" step="0.1" class="hms-input" v-model.number="newVitals.oxygen_saturation" /></div>
                <div class="col-md-3"><label class="hms-label">Blood Sugar</label>
                  <input type="number" step="0.1" class="hms-input" v-model.number="newVitals.blood_sugar" /></div>
                <div class="col-md-3"><label class="hms-label">NEWS2 (0–20)</label>
                  <input type="number" class="hms-input" v-model.number="newVitals.news2_score" min="0" max="20" /></div>
                <div class="col-md-12"><label class="hms-label">Notes</label>
                  <input class="hms-input" v-model="newVitals.notes" /></div>
              </div>
              <button class="hms-btn hms-btn-primary mb-4" @click="saveVitals" :disabled="processing">
                <i class="mdi mdi-content-save me-1"></i>Save Vitals
              </button>

              <div class="hms-section-label">VITALS TREND (this visit, latest first)</div>
              <div v-if="vitalsThis.length" style="overflow-x:auto;">
                <table class="hms-table">
                  <thead><tr><th>Time</th><th>BP</th><th>HR</th><th>RR</th><th>T°</th><th>SpO₂</th><th>Sugar</th><th>NEWS2</th><th>Pain</th></tr></thead>
                  <tbody>
                    <tr v-for="v in vitalsThis" :key="v.vital_id">
                      <td class="hms-muted-sm">{{ formatDateTime(v.recorded_at) }}</td>
                      <td>{{ v.bp_systolic }}/{{ v.bp_diastolic }}</td>
                      <td>{{ v.pulse }}</td>
                      <td>{{ v.respiratory_rate }}</td>
                      <td>{{ v.temperature }}</td>
                      <td>{{ v.oxygen_saturation }}</td>
                      <td>{{ v.blood_sugar || '—' }}</td>
                      <td><span class="hms-badge" :class="news2Badge(v.news2_score)">{{ v.news2_score }}</span></td>
                      <td>{{ v.pain_score }}/10</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="hms-muted">No vitals recorded for this visit yet.</div>
            </div>

            <!-- DIAGNOSES TAB -->
            <div v-if="tab === 'diagnoses'" class="hms-card">
              <div class="hms-section-label">ADD DIAGNOSIS</div>
              <div class="row g-3 mb-3">
                <div class="col-md-10 position-relative"><label class="hms-label">Diagnosis (ICD-10 search) *</label>
                  <input class="hms-input" v-model="icd10Query" @input="onIcd10Input"
                         placeholder="Start typing a diagnosis or ICD-10 code… (e.g. malaria, pneumonia, J18)" autocomplete="off" />
                  <div v-if="icd10Suggest.length" class="hms-suggest" style="position:absolute; z-index:50; left:12px; right:12px; background:#fff; border:1px solid #e0e6ed; border-radius:6px; max-height:280px; overflow:auto;">
                    <div v-for="s in icd10Suggest" :key="s.icd10_code" class="hms-suggest-item"
                         style="padding:8px 12px; cursor:pointer; border-bottom:1px solid #f0f3f8;"
                         @click="pickIcd10(s)">
                      <span class="hms-mono fw-semibold">{{ s.icd10_code }}</span>
                      <span class="ms-2">{{ s.title }}</span>
                      <span v-if="s.is_common" class="hms-badge hms-badge-info ms-2" style="font-size:9pt;">common</span>
                    </div>
                  </div>
                  <div v-if="newDiag.icd_code" class="mt-1 hms-muted-sm">
                    <strong>{{ newDiag.icd_code }}</strong> · {{ newDiag.diagnosis_name }}
                    <button class="hms-btn-icon ms-2" @click="clearIcd10" title="Clear"><i class="mdi mdi-close"></i></button>
                  </div>
                </div>
                <div class="col-md-2"><label class="hms-label">Type</label>
                  <select class="hms-input" v-model="newDiag.diagnosis_type">
                    <option value="primary">Primary</option>
                    <option value="secondary">Secondary</option>
                    <option value="provisional">Provisional</option>
                    <option value="differential">Differential</option>
                  </select>
                </div>
                <div class="col-12"><label class="hms-label">Notes</label>
                  <input class="hms-input" v-model="newDiag.notes" />
                </div>
              </div>
              <div class="d-flex gap-2 mb-4">
                <button class="hms-btn hms-btn-primary" @click="saveDiagnosis" :disabled="processing || !newDiag.diagnosis_name">
                  <i class="mdi mdi-plus me-1"></i>Add Diagnosis
                </button>
                <button class="hms-btn hms-btn-blue" @click="aiIcd" :disabled="aiLoading || !newDiag.diagnosis_name">
                  <i class="mdi mdi-robot-outline me-1"></i>AI ICD Suggest
                </button>
              </div>

              <div v-if="aiIcdResult" class="hms-ai-box mb-3">
                <div class="d-flex align-items-center mb-2">
                  <strong>AI ICD-10 suggestions:</strong>
                  <button class="hms-btn-icon ms-auto" @click="aiIcdResult = null"><i class="mdi mdi-close"></i></button>
                </div>
                <div v-for="(s, idx) in aiIcdResult" :key="idx" class="hms-icd-suggest" @click="newDiag.icd_code = s.code; newDiag.diagnosis_name = s.name">
                  <span class="hms-mono fw-semibold">{{ s.code }}</span> — {{ s.name }}
                </div>
              </div>

              <div class="hms-section-label">DIAGNOSES THIS VISIT ({{ diagThis.length }})</div>
              <div v-if="diagThis.length" class="hms-diag-list mb-4">
                <div v-for="d in diagThis" :key="d.diagnosis_id" class="hms-diag-row">
                  <span class="hms-mono">{{ d.icd_code || '—' }}</span>
                  <strong>{{ d.diagnosis_name }}</strong>
                  <span class="hms-badge hms-badge-info">{{ d.diagnosis_type }}</span>
                  <span class="hms-muted-sm ms-auto">{{ d.doctor_name }} · {{ formatDate(d.diagnosis_date) }}</span>
                </div>
              </div>
              <div v-else class="hms-muted mb-4">No diagnoses yet for this visit.</div>

              <div v-if="diagPrior.length">
                <div class="hms-section-label">PRIOR DIAGNOSES ({{ diagPrior.length }})</div>
                <div class="hms-diag-list">
                  <div v-for="d in diagPrior" :key="d.diagnosis_id" class="hms-diag-row hms-diag-prior">
                    <span class="hms-mono">{{ d.icd_code || '—' }}</span>
                    <span>{{ d.diagnosis_name }}</span>
                    <span class="hms-muted-sm ms-auto">{{ formatDate(d.diagnosis_date) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ORDERS TAB -->
            <div v-if="tab === 'orders'" class="hms-card">
              <div class="hms-tabs hms-tabs-sm mb-3">
                <button class="hms-tab" :class="{ active: orderTab==='lab' }" @click="orderTab='lab'"><i class="mdi mdi-flask me-1"></i>Lab</button>
                <button class="hms-tab" :class="{ active: orderTab==='rad' }" @click="orderTab='rad'"><i class="mdi mdi-radioactive me-1"></i>Radiology</button>
                <button class="hms-tab" :class="{ active: orderTab==='rx'  }" @click="orderTab='rx'"><i class="mdi mdi-pill me-1"></i>Prescription</button>
              </div>

              <!-- LAB ORDERS -->
              <div v-if="orderTab === 'lab'">
                <div class="hms-section-label">NEW LAB REQUEST</div>
                <div class="row g-3 mb-3">
                  <div class="col-md-4"><label class="hms-label">Priority</label>
                    <select class="hms-input" v-model="labOrder.priority">
                      <option value="routine">Routine</option>
                      <option value="urgent">Urgent</option>
                      <option value="stat">STAT</option>
                    </select>
                  </div>
                  <!-- Clinical Notes field removed from lab request (HMIS_Issues) -->
                  <div class="col-12"><label class="hms-label">Pick tests</label>
                    <input class="hms-input" v-model="labCatalogSearch" placeholder="Search test name..." />
                    <div class="hms-pick-list mt-2">
                      <div v-for="t in filteredLabCatalog" :key="t.test_id"
                           class="hms-pick-item" :class="{ picked: labOrder.tests.find(x => x.test_id == t.test_id) }"
                           @click="toggleLabTest(t)">
                        <i class="mdi" :class="labOrder.tests.find(x => x.test_id == t.test_id) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'"></i>
                        <span>{{ t.test_name }} <span class="hms-muted-sm">· {{ t.sample_type }} · KSh {{ t.price }}</span></span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="labOrder.tests.length" class="hms-muted-sm mb-2">{{ labOrder.tests.length }} test(s) selected — total KSh {{ formatNumber(labOrderTotal) }}</div>
                <button class="hms-btn hms-btn-primary" @click="submitLabOrder" :disabled="processing || !labOrder.tests.length">
                  <i class="mdi mdi-flask-plus me-1"></i>Send to Lab
                </button>

                <div v-if="labs.length" class="mt-4">
                  <div class="hms-section-label">EXISTING LAB REQUESTS</div>
                  <div v-for="l in labs" :key="l.request_id" class="hms-order-row">
                    <span class="hms-mono fw-semibold">{{ l.request_number }}</span>
                    <span class="hms-badge" :class="priorityBadge(l.priority)">{{ l.priority }}</span>
                    <span class="hms-badge hms-badge-info">{{ l.status }}</span>
                    <span class="hms-muted-sm">{{ l.resulted }}/{{ l.items }} resulted</span>
                    <span class="hms-muted-sm">{{ formatDateTime(l.created_at) }}</span>
                    <button class="hms-btn hms-btn-sm hms-btn-blue ms-auto" @click="viewLabResults(l)">
                      <i class="mdi mdi-eye me-1"></i>View Results
                    </button>
                  </div>
                </div>
              </div>

              <!-- RADIOLOGY ORDERS -->
              <div v-if="orderTab === 'rad'">
                <div class="hms-section-label">NEW RADIOLOGY REQUEST</div>
                <div class="row g-3 mb-3">
                  <div class="col-md-7"><label class="hms-label">Scan Type *</label>
                    <select class="hms-input" v-model="radOrder.scan_type">
                      <option value="">— Select —</option>
                      <option v-for="s in scanTypes" :key="s" :value="s">{{ s }}</option>
                    </select>
                  </div>
                  <div class="col-md-5"><label class="hms-label">Body Part</label>
                    <input class="hms-input" v-model="radOrder.body_part" placeholder="e.g. Chest" />
                  </div>
                  <div class="col-md-4"><label class="hms-label">Priority</label>
                    <select class="hms-input" v-model="radOrder.priority">
                      <option value="routine">Routine</option>
                      <option value="urgent">Urgent</option>
                      <option value="stat">STAT</option>
                    </select>
                  </div>
                  <div class="col-md-4"><label class="hms-label">Price (KSh)</label>
                    <input type="number" class="hms-input" v-model.number="radOrder.price" min="0" step="50" />
                  </div>
                  <div class="col-md-4 d-flex align-items-end">
                    <label class="hms-toggle">
                      <input type="checkbox" :checked="radOrder.contrast_required == 1" @change="radOrder.contrast_required = $event.target.checked ? 1 : 0" />
                      <span>Contrast required</span>
                    </label>
                  </div>
                  <div class="col-12"><label class="hms-label">Clinical Indication</label>
                    <textarea class="hms-input" rows="2" v-model="radOrder.clinical_indication"></textarea>
                  </div>
                </div>
                <button class="hms-btn hms-btn-primary" @click="submitRadOrder" :disabled="processing || !radOrder.scan_type">
                  <i class="mdi mdi-radioactive me-1"></i>Send to Radiology
                </button>

                <div v-if="radiology.length" class="mt-4">
                  <div class="hms-section-label">EXISTING IMAGING</div>
                  <div v-for="r in radiology" :key="r.radiology_id" class="hms-order-row">
                    <span class="hms-mono fw-semibold">{{ r.request_number }}</span>
                    <span>{{ r.scan_type }}{{ r.body_part ? ' · ' + r.body_part : '' }}</span>
                    <span class="hms-badge" :class="priorityBadge(r.priority)">{{ r.priority }}</span>
                    <span class="hms-badge hms-badge-info">{{ r.status }}</span>
                    <span class="hms-muted-sm ms-auto">{{ formatDateTime(r.created_at) }}</span>
                  </div>
                </div>
              </div>

              <!-- PRESCRIPTION -->
              <div v-if="orderTab === 'rx'">
                <div class="hms-section-label">NEW PRESCRIPTION</div>
                <div v-for="(item, idx) in rxOrder.items" :key="idx" class="hms-rx-item">
                  <div class="row g-2">
                    <div class="col-md-4 hms-drug-search"><label class="hms-label">Drug</label>
                      <input class="hms-input" v-model="item.drug_name" placeholder="Type to search pharmacy stock..." autocomplete="off"
                             @input="onDrugInput(idx)" @focus="onDrugInput(idx)" @blur="closeDrugDropdown()" />
                      <div v-if="drugDropdownIdx === idx && drugMatches.length" class="hms-drug-dropdown">
                        <div v-for="d in drugMatches" :key="d.stock_id" class="hms-drug-option" @mousedown.prevent="pickDrug(idx, d)">
                          <span class="fw-semibold">{{ d.drug_name }}</span>
                          <span v-if="d.strength" class="hms-muted-sm">{{ d.strength }}</span>
                          <span class="ms-auto hms-badge" :class="Number(d.quantity_in_stock) > 0 ? 'hms-badge-success' : 'hms-badge-danger'">
                            {{ Number(d.quantity_in_stock) > 0 ? Number(d.quantity_in_stock) + ' in stock' : 'Out of stock' }}
                          </span>
                        </div>
                      </div>
                      <div v-else-if="drugDropdownIdx === idx && item.drug_name && item.drug_name.length >= 2" class="hms-drug-dropdown">
                        <div class="hms-drug-option hms-muted-sm">Not in inventory — will be prescribed as written</div>
                      </div>
                    </div>
                    <div class="col-md-2"><label class="hms-label">Dosage</label>
                      <input class="hms-input" v-model="item.dosage" placeholder="500mg" />
                    </div>
                    <div class="col-md-2"><label class="hms-label">Frequency</label>
                      <input class="hms-input" v-model="item.frequency" placeholder="TDS" />
                    </div>
                    <div class="col-md-2"><label class="hms-label">Duration</label>
                      <input class="hms-input" v-model="item.duration" placeholder="7 days" />
                    </div>
                    <div class="col-md-2"><label class="hms-label">Route</label>
                      <input class="hms-input" v-model="item.route" placeholder="PO" />
                    </div>
                    <div class="col-md-2"><label class="hms-label">Qty</label>
                      <input type="number" class="hms-input" v-model.number="item.quantity_prescribed" min="1" />
                    </div>
                    <!-- Unit Price removed from Rx form (HMIS_Issues) — price is visible in pharmacy only; still sent silently for billing -->
                    <div class="col-md-1 d-flex align-items-end">
                      <button class="hms-btn hms-btn-sm hms-btn-danger" @click="rxOrder.items.splice(idx,1)"><i class="mdi mdi-trash-can"></i></button>
                    </div>
                  </div>
                </div>
                <button class="hms-btn hms-btn-blue mb-3" @click="rxOrder.items.push({ drug_name:'', dosage:'', frequency:'', duration:'', route:'PO', quantity_prescribed:1, unit_price:0 })">
                  <i class="mdi mdi-plus me-1"></i>Add Drug
                </button>

                <div class="hms-section-label">NOTES</div>
                <textarea class="hms-input mb-3" rows="2" v-model="rxOrder.notes" placeholder="Additional dispensing or counseling notes..."></textarea>

                <button class="hms-btn hms-btn-primary" @click="submitRx" :disabled="processing || !rxOrder.items.length">
                  <i class="mdi mdi-pill me-1"></i>Send to Pharmacy
                </button>

                <div v-if="prescriptions.length" class="mt-4">
                  <div class="hms-section-label">EXISTING PRESCRIPTIONS</div>
                  <div v-for="p in prescriptions" :key="p.prescription_id" class="hms-order-row">
                    <span class="hms-mono fw-semibold">{{ p.prescription_number }}</span>
                    <span>{{ p.items }} item(s)</span>
                    <span class="hms-badge hms-badge-info">{{ p.status }}</span>
                    <span class="hms-muted-sm ms-auto">{{ formatDateTime(p.created_at) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- HISTORY TAB -->
            <div v-if="tab === 'history'" class="hms-card">
              <div class="hms-section-label">PRIOR VISITS ({{ priorVisits.length }})</div>
              <div v-if="priorVisits.length" class="hms-prior-list mb-4">
                <div v-for="pv in priorVisits" :key="pv.visit_id" class="hms-prior-row">
                  <span class="hms-mono">{{ pv.visit_number }}</span>
                  <span class="hms-badge hms-badge-info">{{ pv.visit_type }}</span>
                  <span>{{ truncate(pv.chief_complaint, 60) || '—' }}</span>
                  <span v-if="pv.disposition" class="hms-badge hms-badge-purple">{{ pv.disposition }}</span>
                  <span class="hms-muted-sm ms-auto">{{ formatDate(pv.visit_date) }}</span>
                </div>
              </div>
              <div v-else class="hms-muted mb-4">No prior visits.</div>

              <div class="hms-section-label">PRIOR DIAGNOSES ({{ diagPrior.length }})</div>
              <div v-if="diagPrior.length" class="hms-diag-list">
                <div v-for="d in diagPrior" :key="d.diagnosis_id" class="hms-diag-row hms-diag-prior">
                  <span class="hms-mono">{{ d.icd_code || '—' }}</span>
                  <span>{{ d.diagnosis_name }}</span>
                  <span class="hms-muted-sm ms-auto">{{ formatDate(d.diagnosis_date) }}</span>
                </div>
              </div>
              <div v-else class="hms-muted">No prior diagnoses.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Disposition modal -->
      <div class="hms-modal-overlay" v-if="dispCode" @click.self="dispCode=null">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-check-decagram me-1"></i>Confirm: {{ dispLabel }}</span>
            <button class="hms-modal-close" @click="dispCode=null">&times;</button>
          </div>
          <div class="hms-info-banner mb-3" v-if="dispCode === 'admitted'">
            <i class="mdi mdi-information-outline me-1"></i>After confirming, go to <a href="javascript:void(0)" @click="$router.push('/wards')">Wards & Beds</a> to allocate a ward bed.
          </div>
          <div class="hms-info-banner mb-3" v-if="dispCode === 'referred_out'">
            <i class="mdi mdi-information-outline me-1"></i>Note the receiving facility, mode of transport, and reason in the notes.
          </div>
          <textarea class="hms-input mb-3" rows="3" v-model="dispNotes" placeholder="Notes..."></textarea>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="dispCode=null">Cancel</button>
            <button class="hms-btn hms-btn-primary flex-fill" @click="confirmDispose" :disabled="processing">Confirm</button>
          </div>
        </div>
      </div>

      <!-- ORDER BUNDLES MODAL -->
      <div class="hms-modal-overlay" v-if="showBundles" @click.self="showBundles=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-clipboard-list-outline me-1"></i>Apply Order Bundle</span>
            <button class="hms-modal-close" @click="showBundles=false">&times;</button>
          </div>
          <div class="hms-info-banner mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            One-click create lab + radiology + prescription orders for this patient. Verify clinically before sending.
          </div>

          <div v-if="!selectedBundle" class="hms-bundle-grid">
            <div v-for="b in bundles" :key="b.code" class="hms-bundle-card" :style="{ borderLeftColor: b.color }" @click="selectedBundle = b">
              <i class="mdi" :class="b.icon" :style="{ color: b.color }"></i>
              <div class="flex-fill">
                <div class="hms-bundle-name">{{ b.name }}</div>
                <div class="hms-bundle-summary">{{ b.summary }}</div>
                <div class="hms-bundle-counts">
                  <span v-if="b.lab_count"><i class="mdi mdi-flask"></i> {{ b.lab_count }} lab</span>
                  <span v-if="b.rad_count"><i class="mdi mdi-radioactive"></i> {{ b.rad_count }} imaging</span>
                  <span v-if="b.rx_count"><i class="mdi mdi-pill"></i> {{ b.rx_count }} drugs</span>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="hms-bundle-detail">
            <div class="hms-bundle-head" :style="{ borderLeftColor: selectedBundle.color }">
              <i class="mdi" :class="selectedBundle.icon" :style="{ color: selectedBundle.color }"></i>
              <div>
                <div class="hms-bundle-name">{{ selectedBundle.name }}</div>
                <div class="hms-bundle-summary">{{ selectedBundle.summary }}</div>
              </div>
              <button class="hms-btn hms-btn-blue ms-auto" @click="selectedBundle = null"><i class="mdi mdi-arrow-left me-1"></i>Back</button>
            </div>

            <div class="row g-3 mt-2">
              <div class="col-md-4" v-if="selectedBundle.lab_items.length">
                <div class="hms-section-label"><i class="mdi mdi-flask me-1"></i>LAB ({{ selectedBundle.lab_items.length }})</div>
                <ul class="hms-bundle-list">
                  <li v-for="(t, i) in selectedBundle.lab_items" :key="i">
                    <strong>{{ t.test_name }}</strong> <span class="hms-muted-sm">{{ t.sample_type }}</span>
                  </li>
                </ul>
              </div>
              <div class="col-md-4" v-if="selectedBundle.rad_items.length">
                <div class="hms-section-label"><i class="mdi mdi-radioactive me-1"></i>IMAGING ({{ selectedBundle.rad_items.length }})</div>
                <ul class="hms-bundle-list">
                  <li v-for="(r, i) in selectedBundle.rad_items" :key="i">
                    <strong>{{ r.scan_type }}</strong> <span class="hms-muted-sm">{{ r.body_part }}{{ r.contrast_required == 1 ? ' · contrast' : '' }}</span>
                  </li>
                </ul>
              </div>
              <div class="col-md-4" v-if="selectedBundle.rx_items.length">
                <div class="hms-section-label"><i class="mdi mdi-pill me-1"></i>DRUGS ({{ selectedBundle.rx_items.length }})</div>
                <ul class="hms-bundle-list">
                  <li v-for="(d, i) in selectedBundle.rx_items" :key="i">
                    <strong>{{ d.drug_name }}</strong> <span class="hms-muted-sm">{{ d.dosage }} · {{ d.frequency }} · {{ d.duration }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <div class="d-flex gap-2 mt-3">
              <button class="hms-btn hms-btn-amber flex-fill" @click="selectedBundle = null">Choose Another</button>
              <button class="hms-btn hms-btn-primary flex-fill" @click="applyBundle" :disabled="processing">
                <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
                Apply Bundle to {{ visit.first_name }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>

      <!-- ===== LAB RESULTS MODAL ===== -->
      <div v-if="labResultsModal" class="hms-modal-bg" @click.self="labResultsModal = false">
        <div class="hms-modal" style="width:780px">
          <div class="hms-modal-head">
            <h5>Lab Results — {{ labResultsData.Request && labResultsData.Request.request_number }}</h5>
            <button class="hms-icon-btn" @click="labResultsModal = false"><i class="mdi mdi-close"></i></button>
          </div>
          <div class="hms-modal-body">
            <div v-if="labResultsData.Request" class="hms-info-banner mb-3">
              <strong>{{ labResultsData.Request.patient_name }}</strong> · {{ labResultsData.Request.patient_no }}
              · Priority: {{ labResultsData.Request.priority }}
              · Status: {{ labResultsData.Request.status }}
              · Requested: {{ formatDateTime(labResultsData.Request.created_at) }}
              <div v-if="labResultsData.Request.clinical_notes" class="hms-muted-sm mt-1">
                <i class="mdi mdi-note-text"></i> {{ labResultsData.Request.clinical_notes }}
              </div>
            </div>

            <table class="hms-result-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Result</th>
                  <th>Unit</th>
                  <th>Reference Range</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="it in (labResultsData.Items || [])" :key="it.item_id" :class="resultRowClass(it)">
                  <td>
                    <strong>{{ it.test_name }}</strong>
                    <div v-if="it.sample_type" class="hms-muted-sm">{{ it.sample_type }}</div>
                  </td>
                  <td>
                    <strong v-if="it.result_value">{{ it.result_value }}</strong>
                    <span v-else class="hms-muted-sm">pending</span>
                    <div v-if="it.flag" class="hms-result-flag">{{ it.flag }}</div>
                  </td>
                  <td>{{ it.test_unit || it.unit || '—' }}</td>
                  <td>
                    <div v-if="it.normal_range_male || it.normal_range_female">
                      <span class="hms-muted-sm">M:</span> {{ it.normal_range_male || '—' }}
                      <span class="hms-muted-sm ms-2">F:</span> {{ it.normal_range_female || '—' }}
                    </div>
                    <span v-else class="hms-muted-sm">—</span>
                  </td>
                  <td><span class="hms-badge" :class="resultStatusBadge(it.status)">{{ it.status || 'pending' }}</span></td>
                </tr>
              </tbody>
            </table>

            <div v-if="(labResultsData.Items || []).some(i => i.result_notes)" class="mt-3">
              <div class="hms-section-label">RESULT NOTES</div>
              <div v-for="it in labResultsData.Items.filter(i => i.result_notes)" :key="'n'+it.item_id" class="hms-result-note">
                <strong>{{ it.test_name }}:</strong> {{ it.result_notes }}
              </div>
            </div>
          </div>
          <div class="hms-modal-foot">
            <button class="hms-btn hms-btn-secondary" @click="labResultsModal = false">Close</button>
            <button class="hms-btn hms-btn-blue" @click="printLabResults"><i class="mdi mdi-printer"></i> Print</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
  --acu-1:#dc3545; --acu-2:#fd7e14; --acu-3:#ffc107; --acu-4:#28a745; --acu-5:#1a7fe8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
  --acu-1:#dc3545; --acu-2:#fd7e14; --acu-3:#ffc107; --acu-4:#28a745; --acu-5:#0047AB;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 24px 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-h { color: var(--hms-h); font-size: 18px; font-weight: 700; }
.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; margin-bottom: 16px; }
.hms-empty { text-align: center; padding: 60px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }
.hms-empty-mini { text-align: center; padding: 16px; color: var(--hms-s); font-size: 13px; }
.hms-empty-mini i { font-size: 28px; color: var(--hms-success); display: block; margin-bottom: 6px; }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-tabs-sm .hms-tab { padding: 4px 12px; font-size: 12px; }

.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; }

.hms-q-list { display: flex; flex-direction: column; gap: 6px; max-height: 760px; overflow-y: auto; }
.hms-q-item { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 10px 12px; cursor: pointer; transition: all .15s; }
.hms-q-item:hover { border-color: var(--hms-primary); }
.hms-q-item.active { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-q-item.is-ed { border-left: 4px solid var(--acu-3); }
.hms-q-num  { color: var(--hms-s); }
.hms-q-time { font-size: 11px; color: var(--hms-h); padding: 1px 8px; border: 1px solid var(--hms-br); border-radius: 50px; }
.hms-q-name { color: var(--hms-h); font-weight: 700; font-size: 14px; margin-top: 4px; }
.hms-q-complaint { color: var(--hms-s); font-style: italic; font-size: 12px; margin-top: 4px; }

.hms-acuity-pill { display: inline-flex; align-items: center; justify-content: center; min-width: 28px; height: 22px; border-radius: 6px; color: #fff; font-weight: 800; font-size: 11px; padding: 0 6px; }
.hms-acuity-pill.acu-1_immediate    { background: var(--acu-1); }
.hms-acuity-pill.acu-2_emergent     { background: var(--acu-2); }
.hms-acuity-pill.acu-3_urgent       { background: var(--acu-3); color: #1a1a1a; }
.hms-acuity-pill.acu-4_less_urgent  { background: var(--acu-4); }
.hms-acuity-pill.acu-5_non_urgent   { background: var(--acu-5); }
.hms-acuity-pill.acu-unset          { background: var(--hms-bg3); color: var(--hms-m); }

.hms-warn-strip { background: rgba(220,53,69,.08); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.25); border-radius: 6px; padding: 4px 8px; font-size: 12px; }
.hms-warn-strip i { margin-right: 4px; }

.hms-avatar-lg { width: 56px; height: 56px; border-radius: 50%; background: var(--hms-primary-lt); color: var(--hms-primary); display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 800; flex-shrink: 0; }

.hms-vitals-strip { display: flex; gap: 8px; flex-wrap: wrap; }
.hms-vital-cell { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 8px 14px; text-align: center; min-width: 70px; }
.hms-vital-num { color: var(--hms-h); font-size: 16px; font-weight: 800; }
.hms-vital-lbl { color: var(--hms-s); font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }
.hms-vital-cell.news2-low  { border-color: var(--hms-success); }
.hms-vital-cell.news2-med  { border-color: var(--hms-warning); }
.hms-vital-cell.news2-high { border-color: var(--hms-danger); background: rgba(220,53,69,.08); }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; color: var(--hms-h); font-size: 13px; }
.hms-toggle input { width: 18px; height: 18px; accent-color: var(--hms-primary); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-info { background: rgba(23,162,184,.15); color: var(--hms-info); border: 1px solid rgba(23,162,184,.3); }
.hms-btn-success { background: rgba(40,167,69,.15); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-btn-warning { background: rgba(255,193,7,.15); color: #ca8a04; border: 1px solid rgba(255,193,7,.3); }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-icon { background: none; border: none; color: var(--hms-s); font-size: 16px; cursor: pointer; padding: 0; }
.hms-btn-icon:hover { color: var(--hms-h); }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 8px 10px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 8px 10px; border-bottom: 1px solid var(--hms-br); }

.hms-badge { padding: 2px 8px; border-radius: 50px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #ca8a04; }
.hms-badge-danger  { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }

.hms-prior-note  { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 10px 14px; margin-bottom: 8px; font-size: 13px; color: var(--hms-h); }
.hms-prior-note > div { padding: 2px 0; }
.hms-diag-list, .hms-prior-list, .hms-order-row, .hms-prior-row { display: flex; flex-direction: column; gap: 6px; }
.hms-diag-row, .hms-prior-row, .hms-order-row { display: flex; gap: 10px; align-items: center; padding: 8px 12px; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; font-size: 13px; flex-wrap: wrap; }
.hms-diag-row.hms-diag-prior, .hms-prior-row { opacity: 0.85; }

.hms-pick-list { max-height: 240px; overflow-y: auto; border: 1px solid var(--hms-br); border-radius: 8px; }
.hms-pick-item { display: flex; align-items: center; gap: 8px; padding: 6px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); font-size: 13px; }
.hms-pick-item:last-child { border-bottom: none; }
.hms-pick-item:hover { background: var(--hms-primary-lt); }
.hms-pick-item.picked { background: var(--hms-primary-lt); color: var(--hms-primary); font-weight: 600; }
.hms-pick-item i { color: var(--hms-primary); }

.hms-rx-item { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 12px; margin-bottom: 8px; }

.hms-drug-search { position: relative; }
.hms-drug-dropdown { position: absolute; top: 100%; left: 8px; right: 8px; z-index: 50; background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.18); max-height: 240px; overflow-y: auto; }
.hms-drug-option { display: flex; align-items: center; gap: 8px; padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); font-size: 13px; }
.hms-drug-option:last-child { border-bottom: none; }
.hms-drug-option:hover { background: var(--hms-primary-lt); }

.hms-triage-vitals { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-left: 4px solid var(--hms-primary); border-radius: 8px; padding: 10px 14px; }
.hms-vitals-strip { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; }
.hms-vs-item { display: inline-flex; align-items: center; gap: 5px; font-size: 13px; font-weight: 600; color: var(--hms-h); }
.hms-vs-item i { color: var(--hms-primary); }

.hms-badge-lab { background: rgba(111,66,193,.15); color: #6f42c1; font-size: 10px; }

.hms-alert { border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-alert-amber { background: rgba(255,193,7,.12); border: 1px solid rgba(255,193,7,.5); color: #b8860b; }

.hms-icd-suggest { padding: 8px 12px; cursor: pointer; border-radius: 6px; transition: all .15s; }
.hms-icd-suggest:hover { background: var(--hms-primary-lt); }

.hms-ai-box { background: var(--hms-bg2); border: 1px solid var(--hms-primary-lt); border-left: 4px solid var(--hms-primary); border-radius: 8px; padding: 12px 16px; }
.hms-ai-body { color: var(--hms-h); font-size: 13px; line-height: 1.5; white-space: pre-wrap; }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-info-banner a { color: var(--hms-primary); font-weight: 700; text-decoration: underline; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; }
.hms-modal-sm { max-width: 460px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }

/* Order bundles */
.hms-bundle-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 10px; }
.hms-bundle-card { display: flex; gap: 12px; align-items: flex-start; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-left: 4px solid var(--hms-primary); border-radius: 10px; padding: 12px 14px; cursor: pointer; transition: all .15s; }
.hms-bundle-card:hover { transform: translateY(-2px); border-color: var(--hms-primary); }
.hms-bundle-card > i { font-size: 28px; flex-shrink: 0; margin-top: 2px; }
.hms-bundle-name { color: var(--hms-h); font-weight: 700; font-size: 14px; }
.hms-bundle-summary { color: var(--hms-s); font-size: 12px; margin-top: 4px; line-height: 1.4; }
.hms-bundle-counts { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 8px; font-size: 11px; color: var(--hms-h); }
.hms-bundle-counts span i { color: var(--hms-primary); margin-right: 2px; }
.hms-bundle-detail { padding: 4px; }
.hms-bundle-head { display: flex; gap: 12px; align-items: center; padding: 12px; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-left: 4px solid var(--hms-primary); border-radius: 10px; }
.hms-bundle-head > i { font-size: 32px; flex-shrink: 0; }
.hms-bundle-list { list-style: none; padding: 0; margin: 0; font-size: 13px; }
.hms-bundle-list li { padding: 4px 0; border-bottom: 1px dashed var(--hms-br); }
.hms-bundle-list li:last-child { border-bottom: none; }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

/* ===== Lab Results Modal ===== */
.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 9000; padding: 20px; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 10px; max-height: 92vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,.4); }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid var(--hms-br); display: flex; justify-content: space-between; align-items: center; }
.hms-modal-head h5 { margin: 0; color: var(--hms-h); font-size: 16px; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid var(--hms-br); display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; color: var(--hms-h); font-size: 20px; }

.hms-info-banner { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-left: 4px solid var(--hms-primary); padding: 10px 12px; border-radius: 6px; font-size: 13px; color: var(--hms-h); }
.hms-info-banner strong { color: var(--hms-h); }

.hms-result-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.hms-result-table th { background: var(--hms-bg2); padding: 9px 10px; border-bottom: 1px solid var(--hms-br); font-weight: 600; color: var(--hms-h); text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: .5px; }
.hms-result-table td { padding: 9px 10px; border-bottom: 1px solid var(--hms-br); color: var(--hms-h); vertical-align: top; }
.hms-result-table tr.row-warn   { background: rgba(255,193,7,.08); }
.hms-result-table tr.row-action { background: rgba(220,53,69,.08); }
.hms-result-table strong { color: var(--hms-h); }

.hms-result-flag { display: inline-block; padding: 1px 6px; border-radius: 4px; background: rgba(220,53,69,.15); color: #ff6b6b; font-size: 10px; font-weight: 700; margin-top: 2px; }
.hms-result-note { background: var(--hms-bg2); border-left: 3px solid var(--hms-primary); padding: 8px 12px; border-radius: 4px; margin-bottom: 6px; font-size: 13px; color: var(--hms-h); }
.hms-badge-success { background: rgba(40,167,69,.15); color: #4ade80; }

/* Consultation queue filter (date + presets) */
.hms-consult-filter { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 6px 8px; }
.hms-consult-flbl { font-size: 10px; color: var(--hms-s); text-transform: uppercase; letter-spacing: .5px; margin: 0; }
.hms-consult-dt { padding: 3px 6px !important; font-size: 11px !important; min-width: 110px !important; }
.hms-chip { padding: 3px 8px; font-size: 10px; font-weight: 600; border-radius: 10px; background: var(--hms-bg1); color: var(--hms-s); border: 1px solid var(--hms-br); cursor: pointer; text-transform: uppercase; letter-spacing: .3px; }
.hms-chip:hover { background: var(--hms-bg3); color: var(--hms-h); }
.hms-chip.active { background: var(--hms-primary); color: #fff; border-color: var(--hms-primary); }

/* Inline allergy editor */
.hms-allergy-row { display: flex; align-items: center; gap: 6px; }
.hms-allergy-none { padding: 4px 10px; border-radius: 6px; background: rgba(40,167,69,.08); color: var(--hms-success); font-size: 12px; }
.hms-allergy-editor { background: var(--hms-bg2); border: 1px solid var(--hms-warning); border-radius: 8px; padding: 10px 12px; }
.hms-btn-ghost { background: transparent; color: var(--hms-s); border: 1px solid var(--hms-br); }
.hms-btn-ghost:hover { background: var(--hms-bg3); color: var(--hms-h); }
.hms-chip-sm { padding: 2px 8px; font-size: 10.5px; border-radius: 10px; background: var(--hms-bg3); color: var(--hms-h); border: 1px solid var(--hms-br); cursor: pointer; display: inline-block; margin-top: 2px; text-transform: none; letter-spacing: normal; font-weight: normal; }
.hms-chip-sm:hover { background: var(--hms-primary); color: #fff; border-color: var(--hms-primary); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import SaveAndQueue from "@/components/hms-shared/SaveAndQueue.vue";
import ConsentOtpButton from "@/components/hms-shared/ConsentOtpButton.vue";
import UnpaidBanner from "@/components/hms-shared/UnpaidBanner.vue";
import { executeJson, hms } from "@/api";
import { useI18n } from "@/api/i18n";

const SCAN_TYPES = [
  "X-Ray", "Ultrasound", "CT Scan", "MRI", "Mammography",
  "Fluoroscopy", "Doppler", "Echocardiogram", "DEXA Bone Density",
  "PET Scan", "Angiography", "Nuclear Medicine"
];

export default {
  name: "ConsultationConsole",
  components: { Navigation, SaveAndQueue, ConsentOtpButton, UnpaidBanner },
  data() {
    return {
      loading: false, processing: false, aiLoading: false, callingNext: false,
      scope: 'all',
      queue: [],
      // Search + date/time filter (defaults to today)
      queueSearch: '', _queueSearchTimer: null,
      filterFrom: new Date().toISOString().slice(0,10),
      filterTo:   new Date().toISOString().slice(0,10),
      allergyEdit: { open: false, value: '', busy: false },
      commonAllergyChips: ['Penicillin','Sulpha drugs','NSAIDs','Aspirin','Cephalosporins','Iodine','Latex','Peanuts','Shellfish','No known allergies'],
      i18n: useI18n(),

      visit: null, vitalsThis: [], vitalsLast: null,
      diagThis: [], diagPrior: [], notes: [],
      labs: [], radiology: [], prescriptions: [],
      priorVisits: [], admission: null,

      tab: 'soap',
      orderTab: 'lab',

      soap: { subjective: '', objective: '', assessment: '', plan: '' },
      editingNoteId: null,
      newVitals: { bp_systolic:0, bp_diastolic:0, pulse:0, respiratory_rate:0, temperature:0, oxygen_saturation:0, blood_sugar:0, news2_score:0, pain_score:0, notes:'' },
      newDiag: { icd_code:'', diagnosis_name:'', diagnosis_type:'primary', notes:'' },
      icd10Query: '', icd10Suggest: [], icd10Timer: null,

      labCatalog: [], labCatalogSearch: '',
      labOrder: { priority:'routine', clinical_notes:'', tests: [] },

      scanTypes: SCAN_TYPES,
      radOrder: { scan_type:'', body_part:'', clinical_indication:'', contrast_required:0, priority:'routine', price:0 },

      rxOrder: { items: [], notes: '' },
      pharmacyStock: [], stockLoaded: false,
      drugDropdownIdx: null, drugMatches: [],

      labResultsModal: false,
      labResultsData: { Request: null, Items: [] },

      aiResult: null, aiIcdResult: null,

      // Order bundles
      bundles: [], showBundles: false, selectedBundle: null,

      dispCode: null, dispLabel: '', dispNotes: '',

      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  computed: {
    todayIso() { return new Date().toISOString().slice(0, 10); },
    isTodayFilter() {
      return this.filterFrom === this.todayIso && this.filterTo === this.todayIso;
    },
    filteredLabCatalog() {
      var q = (this.labCatalogSearch || '').toLowerCase();
      if (!q) return this.labCatalog.slice(0, 50);
      return this.labCatalog.filter(t => (t.test_name || '').toLowerCase().includes(q)).slice(0, 100);
    },
    labOrderTotal() {
      return this.labOrder.tests.reduce((s, t) => s + (parseFloat(t.price) || 0), 0);
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.loadQueue();
    this.loadLabCatalog();
    var preselect = this.$route.query.visit;
    if (preselect) this.selectVisit(preselect);
  },
  beforeUnmount() { if (this.toastTimer) clearTimeout(this.toastTimer); },
  methods: {
    t(key, fallback) {
      return (this.i18n && this.i18n.state.strings[key]) || fallback || key;
    },

    // Skip the "No known allergies" / n/a / nil noise so the alert strip only shows real allergens
    hasRealAllergy(v) {
      if (!v) return false;
      var t = String(v).trim().toLowerCase();
      if (!t) return false;
      var noise = ['no known allergies','nka','none','n/a','nil','none known','no allergies','no known allergy'];
      return noise.indexOf(t) === -1;
    },

    // Inline allergy editing for the currently-selected visit
    openAllergyEdit() {
      this.allergyEdit.value = (this.visit && this.visit.allergies) || '';
      this.allergyEdit.busy = false;
      this.allergyEdit.open = true;
    },
    closeAllergyEdit() { this.allergyEdit.open = false; },
    addAllergyChip(a) {
      var cur = (this.allergyEdit.value || '').trim();
      var parts = cur ? cur.split(',').map(function(s){ return s.trim().toLowerCase(); }) : [];
      if (parts.indexOf(a.toLowerCase()) !== -1) return;
      this.allergyEdit.value = cur ? cur + ', ' + a : a;
    },
    markNoAllergies() {
      this.allergyEdit.value = 'No known allergies';
      this.saveAllergies();
    },
    async saveAllergies() {
      if (!this.visit || !this.visit.patient_id) return;
      this.allergyEdit.busy = true;
      try {
        var val = (this.allergyEdit.value || '').trim();
        var r = await executeJson({ function: 'updatePatient', patient_id: this.visit.patient_id, allergies: val }, hms);
        if (r.data && r.data.success) {
          this.visit.allergies = val;
          this.showToast('Allergies updated');
          this.allergyEdit.open = false;
        } else {
          this.showToast((r.data && r.data.message) || 'Update failed', true);
        }
      } catch (e) {
        this.showToast('Network error', true);
      } finally {
        this.allergyEdit.busy = false;
      }
    },

    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    initials(v) { var f=(v.first_name||'').charAt(0); var l=(v.last_name||'').charAt(0); return ((f+l).toUpperCase())||'?'; },
    minutesIn(t) { if (!t) return 0; var d=new Date(t.replace?t.replace(' ','T'):t); return isNaN(d)?0:Math.max(0,Math.floor((Date.now()-d.getTime())/60000)); },
    ageStr(dob) { if (!dob||dob==='0000-00-00'||dob==='1900-01-01') return 'age unknown'; var d=new Date(dob); if (isNaN(d)) return '—'; return Math.floor((Date.now()-d.getTime())/(365.25*24*3600000))+'y'; },
    truncate(s,n) { return (s||'').length>n ? s.substring(0,n)+'...' : (s||''); },
    formatNumber(n) { if (n==null) return '0'; return Number(n).toLocaleString(); },
    formatDate(s) { if (!s) return '—'; var d=new Date(s.replace?s.replace(' ','T'):s); return isNaN(d)?s:d.toLocaleDateString(); },
    formatDateTime(s) { if (!s) return '—'; var d=new Date(s.replace?s.replace(' ','T'):s); return isNaN(d)?s:d.toLocaleString(); },
    acuityShort(l) { return ({ '1_immediate':'L1','2_emergent':'L2','3_urgent':'L3','4_less_urgent':'L4','5_non_urgent':'L5' })[l] || '?'; },
    priorityBadge(p) { return ({ stat:'hms-badge-danger', urgent:'hms-badge-warning', routine:'hms-badge-neutral' })[p] || 'hms-badge-neutral'; },

    async viewLabResults(l) {
      try {
        var r = await executeJson({ function: "getLabRequestDetail", request_id: l.request_id }, hms);
        if (r.data && r.data.success) {
          this.labResultsData = { Request: r.data.data.Request, Items: r.data.data.Items || [] };
          this.labResultsModal = true;
        } else { this.showToast(r.data && r.data.message || "Could not load results", true); }
      } catch (e) { this.showToast("Connection error", true); }
    },
    resultRowClass(it) {
      if (it.flag && /high|critical/i.test(it.flag)) return 'row-action';
      if (it.flag && /low|abnormal/i.test(it.flag)) return 'row-warn';
      return '';
    },
    resultStatusBadge(s) {
      return ({ resulted: 'hms-badge-success', verified: 'hms-badge-success', completed: 'hms-badge-success',
                pending: 'hms-badge-neutral', collected: 'hms-badge-info', cancelled: 'hms-badge-danger' })[s] || 'hms-badge-neutral';
    },
    printLabResults() {
      var rid = this.labResultsData.Request && this.labResultsData.Request.request_id;
      if (rid) window.open(this.$router.resolve({ name: 'print', params: { type: 'lab', id: rid }, query: { auto: '1' } }).href, '_blank');
    },
    news2Class(s) {
      if (s == null) return '';
      if (s >= 7) return 'news2-high';
      if (s >= 5) return 'news2-med';
      return 'news2-low';
    },
    news2Badge(s) {
      if (s == null) return 'hms-badge-neutral';
      if (s >= 7) return 'hms-badge-danger';
      if (s >= 5) return 'hms-badge-warning';
      return 'hms-badge-success';
    },

    async loadQueue() {
      this.loading = true;
      try {
        var r = await executeJson({
          function:  "getConsultationQueue",
          scope:     this.scope,
          search:    this.queueSearch || '',
          date_from: this.filterFrom || '',
          date_to:   this.filterTo || ''
        }, hms);
        if (r.data && r.data.success) this.queue = (r.data.data && r.data.data.Queue) || [];
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    debouncedQueueLoad() {
      if (this._queueSearchTimer) clearTimeout(this._queueSearchTimer);
      var self = this;
      this._queueSearchTimer = setTimeout(function(){ self.loadQueue(); }, 300);
    },
    async callNextPatient() {
      this.callingNext = true;
      try {
        var r = await executeJson({ function: "callNextForModule", module: "consultation" }, hms);
        if (r.data && r.data.success) {
          var t = r.data.data && r.data.data.Ticket;
          this.showToast(r.data.message || 'Next patient called');
          await this.loadQueue();
          if (t && t.patient_id) {
            var row = this.queue.find(function(q){ return String(q.patient_id) === String(t.patient_id); });
            if (row) this.selectVisit(row.visit_id);
            else this.showToast('Ticket ' + t.ticket_number + ' called — no open consultation visit for this patient yet', true);
          }
        } else {
          this.showToast((r.data && r.data.message) || 'No waiting patients', true);
        }
      } catch (e) { this.showToast('Failed to call next patient', true); }
      this.callingNext = false;
    },
    setFilterToday() {
      var t = new Date().toISOString().slice(0,10);
      this.filterFrom = t; this.filterTo = t; this.loadQueue();
    },
    setFilterYesterday() {
      var d = new Date(); d.setDate(d.getDate() - 1);
      var y = d.toISOString().slice(0,10);
      this.filterFrom = y; this.filterTo = y; this.loadQueue();
    },
    setFilterWeek() {
      var end = new Date(); var start = new Date(); start.setDate(end.getDate() - 6);
      this.filterFrom = start.toISOString().slice(0,10);
      this.filterTo   = end.toISOString().slice(0,10);
      this.loadQueue();
    },
    async loadLabCatalog() {
      try {
        var r = await executeJson({ function: "getLabTests" }, hms);
        if (r.data && r.data.success) this.labCatalog = (r.data.data && r.data.data.Tests) || (r.data.data && r.data.data.LabTests) || [];
      } catch(e) {}
    },

    async selectVisit(visitId) {
      this.processing = true; this.aiResult = null; this.aiIcdResult = null;
      try {
        var r = await executeJson({ function: "getEncounterContext", visit_id: visitId }, hms);
        if (r.data && r.data.success) {
          var d = r.data.data;
          this.visit         = d.Visit;
          this.vitalsThis    = d.VitalsThis || [];
          this.vitalsLast    = d.VitalsLast;
          this.diagThis      = d.DiagThis  || [];
          this.diagPrior     = d.DiagPrior || [];
          this.notes         = d.Notes     || [];
          this.labs          = d.Labs      || [];
          this.radiology     = d.Radiology || [];
          this.prescriptions = d.Prescriptions || [];
          this.priorVisits   = d.PriorVisits || [];
          this.admission     = d.Admission;

          // Reset forms
          this.soap = { subjective:'', objective:'', assessment:'', plan:'' };
          this.editingNoteId = null;
          this.newVitals = { bp_systolic:0, bp_diastolic:0, pulse:0, respiratory_rate:0, temperature:0, oxygen_saturation:0, blood_sugar:0, news2_score:0, pain_score:0, notes:'' };
          this.newDiag   = { icd_code:'', diagnosis_name:'', diagnosis_type:'primary', notes:'' };
          this.labOrder  = { priority:'routine', clinical_notes:'', tests: [] };
          this.radOrder  = { scan_type:'', body_part:'', clinical_indication:'', contrast_required:0, priority:'routine', price:0 };
          this.rxOrder   = { items: [], notes: '' };
          this.tab = 'soap';
        } else { this.showToast(r.data.message || 'Failed', true); }
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },

    async markSeen() {
      try {
        var r = await executeJson({ function: "markSeenByDoctor", visit_id: this.visit.visit_id }, hms);
        if (r.data && r.data.success) { this.showToast("Marked as seen"); await this.selectVisit(this.visit.visit_id); }
      } catch(e) { this.showToast("Connection error", true); }
    },

    async aiAssist() {
      this.aiLoading = true; this.aiResult = null;
      try {
        var ctx = "Patient: " + (this.visit.first_name||'') + " " + (this.visit.last_name||'') + ", " + this.ageStr(this.visit.dob) + ", " + this.visit.gender + ".\n";
        if (this.visit.allergies)          ctx += "Allergies: " + this.visit.allergies + "\n";
        if (this.visit.chronic_conditions) ctx += "Chronic: " + this.visit.chronic_conditions + "\n";
        if (this.visit.chief_complaint)    ctx += "Chief complaint: " + this.visit.chief_complaint + "\n";
        if (this.vitalsLast) ctx += "Latest vitals: BP " + this.vitalsLast.bp_systolic + "/" + this.vitalsLast.bp_diastolic + ", HR " + this.vitalsLast.pulse + ", T " + this.vitalsLast.temperature + ", SpO₂ " + this.vitalsLast.oxygen_saturation + ", NEWS2 " + this.vitalsLast.news2_score + "\n";
        if (this.soap.subjective) ctx += "S: " + this.soap.subjective + "\n";
        if (this.soap.objective)  ctx += "O: " + this.soap.objective + "\n";

        var r = await executeJson({ function: "aiClinicalAssist", clinical_context: ctx }, hms);
        if (r.data && r.data.success) this.aiResult = (r.data.data && (r.data.data.assessment || r.data.data.response || r.data.data.assist || r.data.data.raw)) || JSON.stringify(r.data.data);
        else this.showToast(r.data.message || "AI failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.aiLoading = false;
    },

    async aiIcd() {
      this.aiLoading = true; this.aiIcdResult = null;
      try {
        var r = await executeJson({ function: "aiIcdSuggest", clinical_text: this.newDiag.diagnosis_name || this.icd10Query }, hms);
        if (r.data && r.data.success) {
          var raw = r.data.data && r.data.data.suggestions;
          if (Array.isArray(raw)) this.aiIcdResult = raw;
          else if (raw && raw.codes) this.aiIcdResult = raw.codes;
          else this.aiIcdResult = [];
        }
      } catch(e) {}
      this.aiLoading = false;
    },

    onIcd10Input() {
      var self = this;
      if (this.icd10Timer) clearTimeout(this.icd10Timer);
      var q = (this.icd10Query || '').trim();
      if (q.length < 2) { this.icd10Suggest = []; return; }
      this.icd10Timer = setTimeout(async function() {
        try {
          var r = await executeJson({ function: "searchIcd10", q: q }, hms);
          if (r.data && r.data.success) self.icd10Suggest = r.data.data.results || [];
        } catch (e) { self.icd10Suggest = []; }
      }, 250);
    },
    pickIcd10(s) {
      this.newDiag.icd_code = s.icd10_code;
      this.newDiag.diagnosis_name = s.title;
      this.icd10Suggest = [];
      this.icd10Query = s.icd10_code + ' — ' + s.title;
    },
    clearIcd10() {
      this.newDiag.icd_code = '';
      this.newDiag.diagnosis_name = '';
      this.icd10Query = '';
      this.icd10Suggest = [];
    },

    editNote(n) {
      this.editingNoteId = n.note_id;
      this.soap = {
        subjective: n.subjective || '',
        objective:  n.objective  || '',
        assessment: n.assessment || '',
        plan:       n.plan       || ''
      };
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    cancelNoteEdit() {
      this.editingNoteId = null;
      this.soap = { subjective:'', objective:'', assessment:'', plan:'' };
    },

    async saveSoap() {
      if (!(this.soap.subjective || this.soap.objective || this.soap.assessment || this.soap.plan)) {
        this.showToast("Write something in any section first", true); return;
      }
      this.processing = true;
      try {
        var payload = Object.assign({
          function: "saveConsultationNote", patient_id: this.visit.patient_id, visit_id: this.visit.visit_id, note_type: 'soap'
        }, this.soap);
        if (this.editingNoteId) payload.note_id = this.editingNoteId;
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.showToast(this.editingNoteId ? "Note updated" : "Note saved");
          this.editingNoteId = null;
          this.soap = { subjective:'', objective:'', assessment:'', plan:'' };
          // If the clinician selected a "Save & queue to ___" destination, issue the ticket now.
          if (this.$refs.sqSoap && this.visit && this.visit.patient_id) {
            var q = await this.$refs.sqSoap.queueIfSelected(this.visit.patient_id);
            if (q && q.ok) this.showToast("Queued to " + q.dept_name + " (ticket " + q.ticket_number + ")");
          }
          await this.selectVisit(this.visit.visit_id);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    async saveVitals() {
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({
          function: "recordVitals", patient_id: this.visit.patient_id, visit_id: this.visit.visit_id
        }, this.newVitals), hms);
        if (r.data && r.data.success) {
          this.showToast("Vitals saved");
          this.newVitals = { bp_systolic:0, bp_diastolic:0, pulse:0, respiratory_rate:0, temperature:0, oxygen_saturation:0, blood_sugar:0, news2_score:0, pain_score:0, notes:'' };
          await this.selectVisit(this.visit.visit_id);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    async saveDiagnosis() {
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({
          function: "addDiagnosis", patient_id: this.visit.patient_id, visit_id: this.visit.visit_id
        }, this.newDiag), hms);
        if (r.data && r.data.success) {
          this.showToast("Diagnosis added");
          this.newDiag = { icd_code:'', diagnosis_name:'', diagnosis_type:'primary', notes:'' };
          this.aiIcdResult = null;
          await this.selectVisit(this.visit.visit_id);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    toggleLabTest(t) {
      var i = this.labOrder.tests.findIndex(x => x.test_id == t.test_id);
      if (i >= 0) this.labOrder.tests.splice(i, 1);
      else this.labOrder.tests.push({ test_id: t.test_id, test_name: t.test_name, sample_type: t.sample_type, price: t.price });
    },
    async submitLabOrder() {
      this.processing = true;
      try {
        var r = await executeJson({
          function: "requestLabTest", patient_id: this.visit.patient_id, visit_id: this.visit.visit_id,
          priority: this.labOrder.priority, clinical_notes: this.labOrder.clinical_notes, tests: this.labOrder.tests
        }, hms);
        if (r.data && r.data.success) {
          this.showToast("Lab request created");
          this.labOrder = { priority:'routine', clinical_notes:'', tests: [] };
          await this.selectVisit(this.visit.visit_id);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    async submitRadOrder() {
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({
          function: "createRadiologyRequest", patient_id: this.visit.patient_id, visit_id: this.visit.visit_id
        }, this.radOrder), hms);
        if (r.data && r.data.success) {
          this.showToast("Radiology request created");
          this.radOrder = { scan_type:'', body_part:'', clinical_indication:'', contrast_required:0, priority:'routine', price:0 };
          await this.selectVisit(this.visit.visit_id);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    async loadPharmacyStock() {
      if (this.stockLoaded) return;
      this.stockLoaded = true;
      try {
        var r = await executeJson({ function: "getPharmacyStock" }, hms);
        if (r.data && r.data.success) this.pharmacyStock = (r.data.data && r.data.data.Stock) || [];
      } catch(e) { /* autocomplete degrades to free-text */ }
    },
    onDrugInput(idx) {
      this.loadPharmacyStock();
      this.drugDropdownIdx = idx;
      var q = (this.rxOrder.items[idx].drug_name || '').trim().toLowerCase();
      if (q.length < 2) { this.drugMatches = []; return; }
      this.drugMatches = this.pharmacyStock.filter(function(d) {
        return (d.drug_name || '').toLowerCase().includes(q) || (d.generic_name || '').toLowerCase().includes(q);
      }).slice(0, 8);
    },
    pickDrug(idx, d) {
      var item = this.rxOrder.items[idx];
      item.drug_name = d.drug_name + (d.strength ? ' ' + d.strength : '');
      item.unit_price = parseFloat(d.unit_price) || 0;
      item.stock_id = d.stock_id;
      this.drugDropdownIdx = null;
      this.drugMatches = [];
    },
    closeDrugDropdown() {
      var self = this;
      setTimeout(function() { self.drugDropdownIdx = null; self.drugMatches = []; }, 150);
    },

    async submitRx() {
      this.processing = true;
      try {
        var r = await executeJson({
          function: "createPrescription", patient_id: this.visit.patient_id, visit_id: this.visit.visit_id,
          notes: this.rxOrder.notes, items: this.rxOrder.items
        }, hms);
        if (r.data && r.data.success) {
          this.showToast("Prescription sent to pharmacy");
          this.rxOrder = { items: [], notes: '' };
          await this.selectVisit(this.visit.visit_id);
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    async openBundles() {
      this.selectedBundle = null;
      this.showBundles = true;
      if (!this.bundles.length) {
        try {
          var r = await executeJson({ function: "getOrderBundles" }, hms);
          if (r.data && r.data.success) this.bundles = (r.data.data && r.data.data.Bundles) || [];
        } catch(e) { this.showToast("Connection error", true); }
      }
    },
    async applyBundle() {
      if (!this.selectedBundle || !this.visit) return;
      this.processing = true;
      try {
        var r = await executeJson({
          function: "applyOrderBundle",
          bundle_code: this.selectedBundle.code,
          patient_id: this.visit.patient_id,
          visit_id: this.visit.visit_id
        }, hms);
        if (r.data && r.data.success) {
          var c = r.data.data && r.data.data.created || {};
          var bits = [];
          if (c.lab_request)            bits.push(c.lab_request.item_count + ' lab tests');
          if (c.radiology_requests)     bits.push(c.radiology_requests.length + ' imaging');
          if (c.prescription)           bits.push(c.prescription.item_count + ' drugs');
          this.showToast(this.selectedBundle.name + ' applied · ' + bits.join(', '));
          this.showBundles = false;
          this.selectedBundle = null;
          await this.selectVisit(this.visit.visit_id);
        } else { this.showToast(r.data.message || 'Failed', true); }
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },

    dispose(code) {
      this.dispCode = code;
      this.dispLabel = ({ admitted:'Admit', discharged:'Discharge', referred_out:'Refer Out' })[code] || code;
      this.dispNotes = '';
    },
    async confirmDispose() {
      this.processing = true;
      try {
        var r = await executeJson({ function: "setDisposition", visit_id: this.visit.visit_id, disposition: this.dispCode, disposition_notes: this.dispNotes }, hms);
        if (r.data && r.data.success) {
          this.showToast("Disposition: " + this.dispLabel);
          this.dispCode = null;
          this.visit = null;
          await this.loadQueue();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    }
  }
};
</script>
